import {UseQueryResult} from "@tanstack/react-query";
import {
    updateUserAvatar,
    useClientAccountOverview,
    useCustomMailFolders,
    useUpdateAccountOverview
} from "@twigsby/client-data";
import {AccountOverviewType, AccountUpdateType} from "@twigsby/client-data/lib/Types/AccountOverviewTypes";
import {CustomMailFolderType} from "@twigsby/client-data/lib/Types/FolderTypes";
import {AlertBanner, ButtonSubmit, ColourSolid, Modal, UploadFile} from "@twigsby/frontend-framework";
import {Controller, FieldValues, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {AxiosError} from "axios";
import React, {useContext, useEffect, useMemo, useState} from "react";

type UserProviderType = {
    userData: UseQueryResult<AccountOverviewType, Error | AxiosError>;
    userCustomFoldersData: UseQueryResult<Array<CustomMailFolderType>, Error | AxiosError>;

    SelectNewAvatar: () => void;
    UpdateAvatar: (file: File) => void;
}

const UserContext = React.createContext<UserProviderType>({} as UserProviderType);

const UserProvider = (props: any) => {
    const formMethods = useForm({
        defaultValues: {
            avatar: undefined,
        },
        mode: 'onChange'
    });

    const userData = useClientAccountOverview();
    const userCustomFoldersData = useCustomMailFolders();

    const updateAccountOverview = useUpdateAccountOverview();

    const [ selectAvatarOpen, setSelectAvatarOpen ] = useState<boolean>(false);
    const [ avatarError, setAvatarError ] = useState<any>(null);

    const SelectNewAvatar = () => {
        setSelectAvatarOpen(true);
    }

    const UpdateAvatar = (data: FieldValues) => {
        const formData = new FormData();

        formData.append('file', data.avatar[0], data.avatar[0].name);

        updateUserAvatar(formData)
            .then((r) => {
                if(r.url) {
                    updateAccountOverview.mutate({
                        newData: r.url,
                        updateType: AccountUpdateType.AVATAR
                    })
                }
                setSelectAvatarOpen(false);
                setAvatarError(null);
            })
            .catch((ex) => {
                setAvatarError(ex);
                console.log(ex);
            })
    }

    const userMemo = useMemo(() => {
        return {
            userData,
            userCustomFoldersData,

            SelectNewAvatar,
            UpdateAvatar
        }
    }, [userData, userCustomFoldersData]);

    useEffect(() => {
        return (() => formMethods.reset());
    }, []);

    return (
        <UserContext.Provider value={userMemo}>
            {props.children}

            <Modal title={'Select Avatar'} isOpen={selectAvatarOpen} closeCallback={() => setSelectAvatarOpen(false)}>
                <FormProvider {...formMethods} >
                    <form onSubmit={formMethods.handleSubmit((data) => UpdateAvatar(data))}>
                        <fieldset>
                            {avatarError && (
                                <AlertBanner title={avatarError.toString()} />
                            )}

                            <Controller
                                control={formMethods.control}
                                name={'avatar'}
                                rules={{ required: true }}
                                render={({ field, fieldState }) =>
                                    <UploadFile
                                        extensionDisplayValue={'images'}
                                        accept={'.png,.jpg,.jpeg,.webp,.gif'}
                                        onChange={(e) => field.onChange(e)}
                                        singleFileOnly={true}
                                    />
                                }
                            />
                            <ButtonSubmit
                                title={'Save'}
                                fullWidth={true}
                                colour={ColourSolid.Primary}
                                disabled={!formMethods.formState.isValid}
                            />
                        </fieldset>
                    </form>
                </FormProvider>
            </Modal>

        </UserContext.Provider>
    )
}

const useUser = () => useContext(UserContext);

export { UserContext, useUser };

export default UserProvider
